import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ADD_CLIENTS_BULK, API_BRANCHES, API_CENTERS } from '../Config/ApiUrlConstant';
import SuccessModal from '../Common/SuccessModal';
import { apiJson } from '../utils/apiClient';
import { confirmAction } from '../utils/confirmAction';

const emptyClient = () => ({
  client_name: '', client_dob: '', contact_no: '', client_uid: '', client_pan: '',
  client_business: '', purpose_of_loan: '', client_address: '', client_father_name: '',
  client_mother_name: '', client_husband_name: '', client_husband_dob: '',
  husband_father_name: '', husband_mother_name: '', husband_address: '',
});

const copiedFields = [
  'client_business', 'purpose_of_loan', 'client_address', 'client_father_name',
  'client_mother_name', 'client_husband_name', 'client_husband_dob',
  'husband_father_name', 'husband_mother_name', 'husband_address',
];

const BulkClientDetailsForm = () => {
  const navigate = useNavigate();
  const [branchId, setBranchId] = useState('');
  const [centerId, setCenterId] = useState('');
  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [clients, setClients] = useState([emptyClient()]);
  const [message, setMessage] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const centerRequestId = useRef(0);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const { data } = await apiJson(API_BRANCHES, { method: 'POST', body: JSON.stringify({}) });
        setBranches(data?.data || []);
      } catch (error) {
        setMessage('Unable to load branches.');
        setModalVisible(true);
      }
    };

    void loadBranches();
  }, []);

  const handleBranchChange = async (event) => {
    const requestId = ++centerRequestId.current;
    const value = event.target.value;
    setBranchId(value);
    setCenterId('');
    setCenters([]);
    if (!value) return;

    try {
      const { data } = await apiJson(API_CENTERS, {
        method: 'POST',
        body: JSON.stringify({ is_all: false, branch_id: value }),
      });
      if (requestId === centerRequestId.current) setCenters(data?.data || []);
    } catch (error) {
      if (requestId === centerRequestId.current) {
        setMessage('Unable to load centers for the selected branch.');
        setModalVisible(true);
      }
    }
  };

  const updateClient = (index, field, value) => {
    setClients((current) => current.map((client, rowIndex) => (
      rowIndex === index ? { ...client, [field]: value } : client
    )));
  };

  const addClientRow = () => {
    if (clients.length < 50) setClients((current) => [...current, emptyClient()]);
  };

  const removeClientRow = (index) => {
    if (clients.length > 1) setClients((current) => current.filter((_, rowIndex) => rowIndex !== index));
  };

  const copyPrevious = (index) => {
    if (!index) return;
    setClients((current) => current.map((client, rowIndex) => (
      rowIndex === index
        ? { ...client, ...Object.fromEntries(copiedFields.map((field) => [field, current[index - 1][field]])) }
        : client
    )));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!confirmAction(`add ${clients.length} clients`)) return;
    setIsSaving(true);
    try {
      const { data } = await apiJson(API_ADD_CLIENTS_BULK, {
        method: 'POST',
        body: JSON.stringify({ branch_id: branchId, center_id: centerId, clients }),
      });
      setMessage(data?.message || 'Bulk client creation completed.');
      setModalVisible(true);
      if (data?.status) {
        setClients([emptyClient()]);
        setCenterId('');
      }
    } catch (error) {
      setMessage('Unable to save bulk clients. Check the required fields and try again.');
      setModalVisible(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container my-5">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">Bulk Client Creation</h2>
          <small className="text-muted">Create up to 50 clients for one center. Documents can be added later from client edit.</small>
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/clients')}>Back to Clients</button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label>Branch Name *</label>
            <select className="form-control" value={branchId} onChange={handleBranchChange} required>
              <option value="">Select Branch</option>
              {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branch_name}</option>)}
            </select>
          </div>
          <div className="col-md-6">
            <label>Center Name *</label>
            <select className="form-control" value={centerId} onChange={(event) => setCenterId(event.target.value)} required disabled={!branchId}>
              <option value="">Select Center</option>
              {centers.map((center) => <option key={center.id} value={center.id}>{center.center_name}</option>)}
            </select>
          </div>
        </div>

        {clients.map((client, index) => (
          <div className="card mb-3" key={index}>
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <strong>Client {index + 1}</strong>
              <div className="d-flex gap-2">
                {index > 0 ? <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => copyPrevious(index)}>Copy Previous Details</button> : null}
                {clients.length > 1 ? <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeClientRow(index)}>Remove</button> : null}
              </div>
            </div>
            <div className="card-body row g-3">
              {[
                ['client_name', 'Client Name *', 'text', true], ['client_dob', 'Client DOB *', 'date', true],
                ['contact_no', 'Contact Number *', 'text', true], ['client_business', 'Client Business *', 'text', true],
                ['purpose_of_loan', 'Purpose of Loan *', 'text', true], ['client_uid', 'Client UID', 'text', false],
                ['client_pan', 'Client PAN', 'text', false], ['client_father_name', 'Father Name', 'text', false],
                ['client_mother_name', 'Mother Name', 'text', false], ['client_husband_name', 'Husband Name', 'text', false],
                ['client_husband_dob', 'Husband DOB', 'date', false], ['husband_father_name', 'Husband Father Name', 'text', false],
                ['husband_mother_name', 'Husband Mother Name', 'text', false],
              ].map(([field, label, type, required]) => (
                <div className="col-md-4" key={field}>
                  <label>{label}</label>
                  <input type={type} className="form-control" value={client[field]} required={required} onChange={(event) => updateClient(index, field, event.target.value)} />
                </div>
              ))}
              <div className="col-md-6">
                <label>Client Address *</label>
                <textarea className="form-control" value={client.client_address} required onChange={(event) => updateClient(index, 'client_address', event.target.value)} />
              </div>
              <div className="col-md-6">
                <label>Husband Address</label>
                <textarea className="form-control" value={client.husband_address} onChange={(event) => updateClient(index, 'husband_address', event.target.value)} />
              </div>
            </div>
          </div>
        ))}

        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-outline-primary" onClick={addClientRow} disabled={clients.length >= 50}>Add Client Row</button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>{isSaving ? 'Saving...' : `Create ${clients.length} Client${clients.length > 1 ? 's' : ''}`}</button>
        </div>
      </form>
      {modalVisible ? <SuccessModal message={message} onClose={() => setModalVisible(false)} /> : null}
    </div>
  );
};

export default BulkClientDetailsForm;
